import {
    Table,
} from 'antd';
import cn from 'classnames';
import {omit, pickBy} from 'lodash-es';
import React, {useMemo, useState} from 'react';

import {SimpleActionButton} from 'components/form/components';
import '../../table-row-drillup-content.less';
import {renderColumns} from 'components/table-report/table-report-columns';
import {ITableReportColumnProps} from 'components/table-report/table-report-columns/table-report-columns';
import {ITableReportProps} from 'components/table-report/table-report-types';
import {createStringDate} from 'components/table/utils/table.utils';
import {TEntityValue} from 'modules/data';
import {DATE_DMY} from 'shared/constants/date-format';
import {showMessage} from 'shared/utils';
import {fetchDrillUpTree} from 'store/slices/table-report-slice/table-report-slice-api';
import {TDrillUpParentDocument, TDrillUpChildDocument} from 'store/slices/table-report-slice/table-report-slice-types';

import {CHILD_TYPES, DOCUMENT_COLUMNS} from '../../table-row-drillup-content-constants';
import {TDrillUpRow} from '../../table-row-drillup-content-model';
import {RowChildrenContainer} from '../row-children-container/row-children-container';
import {mapReportColumns} from './expandable-item-utils';

export interface IExpandableItemProps {
    fieldKey: number;
    isNestedChild?: boolean;
    isLastChild?: boolean;
    field: TDrillUpRow;
    columnProps: ITableReportColumnProps;
    docId: ITableReportProps['docId'];
    isRowLoading: boolean;
    setIsRowLoading: (isRowLoading: boolean) => void;
    isFirstLevel?: boolean;
    childType?: string;
}

export const ExpandableItem = ({
    fieldKey,
    isNestedChild,
    field,
    columnProps,
    isLastChild,
    docId,
    isRowLoading,
    setIsRowLoading,
    isFirstLevel,
    childType,
}: IExpandableItemProps) => {
    const isDocument = childType === CHILD_TYPES.DOCUMENT;
    const isRow = childType === CHILD_TYPES.ROW;
    const isMessage = childType === CHILD_TYPES.MESSAGE;

    const [shouldFetchChildren, setShouldFetchChildren] = useState<boolean>(isRow);
    const [isOpenChild, setIsOpenChild] = useState<boolean>(!isFirstLevel && !isRow);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

    const [children, setChildren] = useState<Partial<TDrillUpParentDocument & TDrillUpChildDocument>[]>(
        field?.children ?? [],
    );
    const mappedReportColumns = useMemo(() => (
        field?.reportColumns ? mapReportColumns(field?.reportColumns) : null
    ), [field?.reportColumns]);

    const columnPropsMapped = useMemo(() => {
        const columnPropsFunctions = pickBy(columnProps ?? {}, value => typeof value === 'function');
        return {
            ...columnPropsFunctions,
            rowFormats: [],
            headerFontSize: columnProps?.headerFontSize,
            tableFontSize: columnProps?.tableFontSize,
            docId: field?.docId,
            templateCode: field?.templateCode,
        };
    }, [columnProps, field?.docId, field?.templateCode]);

    const fetchChildren = async () => {
        const {rowIdentifier} = field ?? {};

        if (!docId || !rowIdentifier?.key || !rowIdentifier?.value) {
            return;
        }

        setIsLoadingData(true);
        setIsRowLoading(true);

        try {
            const response = await fetchDrillUpTree(
                docId,
                rowIdentifier?.key,
                rowIdentifier?.value,
            );

            const hasChildren = response.data?.parentDocuments && response.data?.parentDocuments?.length;

            if (!hasChildren && response.data?.message) {
                setChildren([{message: response.data?.message}]);
            } else {
                setChildren(response.data?.parentDocuments || []);
            }
            setShouldFetchChildren(false);
            setIsOpenChild(true);
        } catch (error) {
            console.error('Error fetching children:', error);
            setChildren([]);
        } finally {
            setIsLoadingData(false);
            setIsRowLoading(false);
        }
    };

    const handleClickChildren = () => {
        if (!shouldFetchChildren) {
            setIsOpenChild(prev => !prev);
            return;
        }

        if (isRowLoading) {
            showMessage(
                {message: 'Невозможно раскрыть строку, пока идет загрузка данных другой строки', isError: true},
            );
            return;
        }

        fetchChildren();
    };

    const handleNavigateToDocument = () => {
        const ddPath = [
            '/table-reports',
            ...(field?.docId ? [`/${field?.docId}`] : []),
            `/${field?.templateCode}`,
        ].join('');

        columnProps?.pushWithBreadcrumbs({
            name: field?.docType ?? 'Неизвестно',
            path: ddPath,
            extraData: {
                tableReportLocationDefaults: {
                    locationFilters: field?.filters ?? undefined,
                },
            },
        });
    };

    const dataSource = useMemo(() => {
        if (isDocument) {
            const document = {
                ...field,
                ...(field?.createdDate && {
                    createdDate: createStringDate(field?.createdDate, DATE_DMY),
                }),
            } as Record<string, TEntityValue>;

            return [omit(document, 'children')];
        }
        if (field?.data) {
            return [field.data];
        }
        return [];
    }, [field?.data, isDocument]);

    const getCollapseButtonIcon = () => {
        if (isLoadingData) {
            return 'LoadingOutlined';
        }

        return isOpenChild ? 'MinusCircleOutlined' : 'PlusCircleOutlined';
    };

    const shouldShowCollapseButton = (children?.length || shouldFetchChildren) && !isMessage;

    return (
        <>
            <div
                className="d-flex"
            >
                <div className={cn('line-vertical', {
                    'margin-left': isNestedChild,
                    'last-child': isLastChild,
                })}
                />
                <div className="drillup-grid-container">
                    <div
                        className="expandable-item__content"
                        key={fieldKey}
                    >
                        <div className="line-horizontal" />
                        <div
                            className="list__items"
                        >

                            {shouldShowCollapseButton && (
                                <div className="collapse-button-container">
                                    <SimpleActionButton
                                        type="link"
                                        onClick={handleClickChildren}
                                        className="collapse-action-btn"
                                        icon={getCollapseButtonIcon()}
                                    />
                                    {isOpenChild && (
                                        <div className="row-vertical-line" />
                                    )}
                                </div>
                            )}

                            <div>
                                <div className="expandable-item__header">
                                    {isMessage ? (
                                        <span className="field-message">{field?.message}</span>
                                    ) : (
                                        <h2 className="field-title">{isDocument ? 'Документ' : 'Строка'}</h2>
                                    )}

                                    {(!isRow && !isMessage) && (
                                        <SimpleActionButton
                                            title="перейти"
                                            type="primary"
                                            className="header-button"
                                            onClick={handleNavigateToDocument}
                                        />
                                    )}
                                </div>

                                {!isMessage && (
                                    <Table
                                        dataSource={dataSource}
                                        pagination={{hideOnSinglePage: true}}
                                        className="table-report__table"
                                    >
                                        {renderColumns({
                                            headerStructure: field?.excelTableHeaderStructure!,
                                            columns: mappedReportColumns?.length
                                                ? mappedReportColumns : DOCUMENT_COLUMNS,
                                            columnProps: columnPropsMapped as unknown as ITableReportColumnProps,
                                            disableActions: true,
                                        })}
                                    </Table>
                                )}
                            </div>
                        </div>
                    </div>

                    <RowChildrenContainer
                        isOpen={isOpenChild}
                        isNestedChild
                        fieldChildren={children}
                        columnProps={columnProps}
                        docId={docId}
                        isRowLoading={isRowLoading}
                        setIsRowLoading={setIsRowLoading}
                    />
                </div>
            </div>
        </>
    );
};
